import { useQuery as useTanstackQuery } from '@tanstack/react-query';

import { useAPIClient } from '@src/api';

import { getPriceEstimate } from './priceEstimateApi';


/**
 * Query-key factory for the price estimate (M14 #412). Keyed on the three inputs
 * the estimate depends on, under a `pricing/estimate` namespace so it never
 * collides with the URL-derived keys the generic `useBaseQuery` builds.
 */
function priceEstimateKey({ partType, ymm, condition }) {
  return ['root', 'pricing', 'estimate', partType || '', ymm || '', condition || ''];
}


/**
 * Advisory AI price-estimate hook (Slice P1b). Wraps TanStack Query directly
 * (rather than the URL-based `useBaseQuery`) so it can use the hand-written
 * `getPriceEstimate` api function with its local snake→camel mapper + 204→null
 * handling.
 *
 * Fires ONLY when all three inputs are present (`enabled`); the api function
 * short-circuits to `null` otherwise, but gating `enabled` avoids a pointless
 * query row. Pricing is not per-request volatile, so a generous `staleTime`
 * (5 min) keeps the chip stable while the seller edits the rest of the form.
 *
 * @returns the TanStack query result; `data` is the camelCased estimate or
 *   `null` (no estimate / 204 / incomplete inputs).
 */
function usePriceEstimate({ partType, ymm, condition }) {
  const apiClient = useAPIClient();
  const enabled = Boolean(partType && ymm && condition);

  return useTanstackQuery({
    queryKey: priceEstimateKey({ partType, ymm, condition }),
    queryFn: ({ signal }) => getPriceEstimate({
      apiClient, partType, ymm, condition, signal,
    }),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}


export { priceEstimateKey, usePriceEstimate };
export default usePriceEstimate;
